import {
  Body,
  Box,
  BoxProps,
  Heading,
  Link,
  LinkProps,
  Stack,
  TextInput,
  VStack,
} from '@hover/blueprint';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { HOVER_DOWNLOAD_APP_LINK } from '../../../constants';
import Smartphone from '../../../assets/svgs/Smartphone';
import ArrowButton from '../../ui/ArrowButton';

const VALIDATION = /^(()?\d{3}())?(-|\s)?\d{3}(-|\s)?\d{4}$/;

interface Props extends BoxProps {
  inputBackground?: string;
  contentColor?: string;
  inputColor?: string;
  description?: string;
  linkColorScheme?: LinkProps['colorScheme'];
}

const SignUpCTAForm: React.FC<Props> = ({
  inputBackground,
  contentColor,
  inputColor,
  description,
  linkColorScheme,
}) => {
  const {
    register,
    formState: { errors },
    watch,
    handleSubmit,
  } = useForm();

  const [loading, setLoading] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);

  const getDashedValue = (): string => {
    const { phoneNumber } = watch();
    if (phoneNumber && typeof phoneNumber === 'string') {
      const numWithoutDashes = phoneNumber.replace(/[^0-9]/g, '');
      const numLength = numWithoutDashes.length;
      if (numLength) {
        if (numLength > 0 && numLength < 4) {
          return `${numWithoutDashes.slice(0, 3)}`;
        }
        if (numLength > 3 && numLength < 7) {
          return `${numWithoutDashes.slice(0, 3)}-${numWithoutDashes.slice(
            3,
            6,
          )}`;
        }
        if (numLength > 6) {
          return `${numWithoutDashes.slice(0, 3)}-${numWithoutDashes.slice(
            3,
            6,
          )}-${numWithoutDashes.slice(6)}`;
        }
      }
      return '';
    }
    return '';
  };

  const onSubmit = ({ phoneNumber }: { phoneNumber: string }) => {
    setLoading(true);
    if (phoneNumber.length) {
      setLoading(true);
      const bodyParams = JSON.stringify({
        number: phoneNumber.replaceAll('-', ''),
      });

      fetch('https://hover.to/api/v1/users/send_homeowner_sms', {
        headers: {
          'Content-Type': 'application/json',
        },
        method: 'post',
        body: bodyParams,
      })
        .then(res => {
          setLoading(false);
          if (res.status === 200) {
            setShowSuccessMessage(true);
          }
        })
        .catch(() => {
          setLoading(false);
        });
    }
  };

  return (
    <Stack
      direction={{ base: 'column', tablet: 'row' }}
      pt={300}
      alignItems={{ base: 'center', tablet: 'flex-start' }}
      spacing={{ base: errors?.phoneNumber ? '30px' : '78px', tablet: 400 }}
    >
      {!showSuccessMessage ? (
        <>
          <Box alignItems="flex-start">
            <Box
              py={300}
              px={400}
              bg={inputBackground}
              alignItems="center"
              borderRadius="4px"
              mr={200}
              borderWidth="1px"
              borderStyle="solid"
              borderColor="neutral.300"
            >
              <>🇺🇸</>
              <Heading ml={200} size={200} marginBottom={0}>
                +1
              </Heading>
            </Box>
            <VStack spacing="10px" w={{ base: '161px', tablet: '134px' }}>
              <TextInput
                bg={inputBackground}
                {...register('phoneNumber', {
                  required: 'error message',
                  pattern: VALIDATION,
                })}
                value={getDashedValue()}
                borderColor="neutral.0"
                color={inputColor}
                fontWeight={400}
                borderWidth="1px"
                placeholder="818-123-4567"
                _placeholder={{
                  color: 'brandNavy.300',
                }}
                borderStyle="solid"
              />
              {errors?.phoneNumber ? (
                <Body
                  size={300}
                  color="danger.300"
                  maxW="188px"
                  alignSelf="center"
                >
                  Please enter a U.S. phone number in the correct format. All
                  numbers, no dashes.
                </Body>
              ) : null}
            </VStack>
          </Box>
          <VStack
            maxW={{ base: '100%', tablet: '252px' }}
            w="100%"
            spacing={300}
            alignItems={{ base: 'center', tablet: 'flex-start' }}
          >
            <ArrowButton
              isLoading={loading}
              onClick={handleSubmit(onSubmit)}
              w="100%"
            >
              GET STARTED
            </ArrowButton>
            <Body
              textAlign={{ base: 'center', tablet: 'left' }}
              size={300}
              color={contentColor}
              maxW="188px"
              alignSelf="center"
            >
              We’ll text you a link to download&nbsp;
              <Link
                colorScheme={linkColorScheme}
                href={HOVER_DOWNLOAD_APP_LINK}
                textDecorationColor="currentColor"
              >
                our app
              </Link>{' '}
              From there, it only takes a few minutes. {description}
            </Body>
          </VStack>
        </>
      ) : (
        <Box
          bg="primary.100"
          py={200}
          pl={400}
          pr={300}
          d="flex"
          borderRadius="4px"
          borderWidth="1px"
          borderStyle="solid"
          borderColor="neutral.300"
          alignItems="center"
        >
          <Body color="brandNavy.500" size={400}>
            Check your phone to get started.
          </Body>
          <Box ml={200}>
            <Smartphone />
          </Box>
        </Box>
      )}
    </Stack>
  );
};

SignUpCTAForm.defaultProps = {
  inputBackground: 'neutral.0',
  contentColor: 'neutral.0',
  inputColor: 'brandNavy.300',
  description: '',
  linkColorScheme: 'neutral',
};

export default SignUpCTAForm;
